import { create } from 'zustand';
import fetchWithTenant from '../utils/fetchWithTenant';

const useMapStore = create((set, get) => ({
  // State
  settings: null,
  devices: [],
  floorPlans: [],
  selectedDevice: null,
  viewMode: 'map', // 'map' or 'floorplan'
  loading: false,
  error: null,

  // ---------- Settings ----------
  fetchSettings: async () => {
    try {
      set({ loading: true, error: null });
      const res = await fetchWithTenant('/api/map/settings');
      if (!res.ok) throw new Error('Failed to fetch map settings');
      const data = await res.json();
      set({ settings: data.settings, loading: false });
      return data;
    } catch (err) {
      console.error('[mapStore] fetchSettings error:', err);
      set({ error: err.message, loading: false });
      return null;
    }
  },

  updateSettings: async (updates) => {
    try {
      set({ error: null });
      const res = await fetchWithTenant('/api/map/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error('Failed to update map settings');
      const data = await res.json();
      set({ settings: data.settings });
      return data;
    } catch (err) {
      console.error('[mapStore] updateSettings error:', err);
      set({ error: err.message });
      return null;
    }
  },

  // ---------- Device Locations ----------
  fetchDeviceLocations: async (filters = {}) => {
    try {
      set({ loading: true, error: null });
      const params = new URLSearchParams();
      if (filters.deviceType) params.set('deviceType', filters.deviceType);
      if (filters.hasGeo !== undefined) params.set('hasGeo', filters.hasGeo);
      if (filters.floorPlanId) params.set('floorPlanId', filters.floorPlanId);
      const query = params.toString() ? `?${params.toString()}` : '';
      const res = await fetchWithTenant(`/api/map/devices${query}`);
      if (!res.ok) throw new Error('Failed to fetch device locations');
      const data = await res.json();
      set({ devices: data.locations || [], loading: false });
      return data;
    } catch (err) {
      console.error('[mapStore] fetchDeviceLocations error:', err);
      set({ error: err.message, loading: false });
      return [];
    }
  },

  upsertDeviceLocation: async (deviceData) => {
    try {
      set({ error: null });
      const res = await fetchWithTenant('/api/map/devices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deviceData),
      });
      if (!res.ok) throw new Error('Failed to save device location');
      const data = await res.json();
      const saved = data.location;
      
      // Update local state — replace if exists, add if new
      set((state) => {
        const idx = state.devices.findIndex((d) => d.id === saved.id);
        const updated = [...state.devices];
        if (idx >= 0) {
          updated[idx] = saved;
        } else {
          updated.push(saved);
        }
        return { devices: updated };
      });
      return saved;
      
    } catch (err) {
      console.error('[mapStore] upsertDeviceLocation error:', err);
      set({ error: err.message });
      return null;
    }
  },

  bulkUpsertDeviceLocations: async (devicesArray) => {
    try {
      set({ error: null });
      const res = await fetchWithTenant('/api/map/devices/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ devices: devicesArray }),
      });
      if (!res.ok) throw new Error('Failed to bulk save device locations');
      const data = await res.json();
      // Refresh the full list after bulk update
      get().fetchDeviceLocations();
      return data;
    } catch (err) {
      console.error('[mapStore] bulkUpsertDeviceLocations error:', err);
      set({ error: err.message });
      return null;
    }
  },

  deleteDeviceLocation: async (id) => {
    try {
      set({ error: null });
      const res = await fetchWithTenant(`/api/map/devices/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete device location');
      set((state) => ({
        devices: state.devices.filter((d) => d.id !== id),
        selectedDevice: state.selectedDevice?.id === id ? null : state.selectedDevice,
      }));
      return true;
    } catch (err) {
      console.error('[mapStore] deleteDeviceLocation error:', err);
      set({ error: err.message });
      return false;
    }
  },

  // ---------- Floor Plans ----------
  fetchFloorPlans: async () => {
    try {
      set({ loading: true, error: null });
      const res = await fetchWithTenant('/api/map/floorplans');
      if (!res.ok) throw new Error('Failed to fetch floor plans');
      const data = await res.json();
      set({ floorPlans: data.floorPlans || [], loading: false });
      return data;
    } catch (err) {
      console.error('[mapStore] fetchFloorPlans error:', err);
      set({ error: err.message, loading: false });
      return [];
    }
  },

  // ---------- UI State ----------
  setSelectedDevice: (device) => set({ selectedDevice: device }),
  clearSelectedDevice: () => set({ selectedDevice: null }),
  setViewMode: (mode) => set({ viewMode: mode }),
  // ---------- File Upload ----------
  uploadFloorPlanImage: async (file) => {
    try {
      set({ error: null });
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetchWithTenant("/api/map/upload", {
        method: "POST",
        body: formData,
      });
      if (!res.ok) throw new Error("Failed to upload file");
      const data = await res.json();
      return data.file;
    } catch (err) {
      console.error("[mapStore] uploadFloorPlanImage error:", err);
      set({ error: err.message });
      return null;
    }
  },

  // ---------- Create Survey (FloorPlan) ----------
  createSurvey: async (surveyData) => {
    try {
      set({ error: null });
      const res = await fetchWithTenant("/api/map/floorplans", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(surveyData),
      });
      if (!res.ok) throw new Error("Failed to create survey");
      const data = await res.json();
      set((state) => ({ floorPlans: [...state.floorPlans, data.floorPlan] }));
      return data.floorPlan;
    } catch (err) {
      console.error("[mapStore] createSurvey error:", err);
      set({ error: err.message });
      return null;
    }
  },

  // ---------- Delete Survey ----------
  deleteSurvey: async (id) => {
    try {
      set({ error: null });
      const res = await fetchWithTenant("/api/map/floorplans/" + id, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete survey");
      set((state) => ({ floorPlans: state.floorPlans.filter((f) => f.id !== id) }));
      return true;
    } catch (err) {
      console.error("[mapStore] deleteSurvey error:", err);
      set({ error: err.message });
      return false;
    }
  },
  // ---------- Fetch Single Floor Plan ----------
  fetchFloorPlanById: async (id) => {
    try {
      set({ loading: true, error: null });
      const res = await fetchWithTenant("/api/map/floorplans/" + id);
      if (!res.ok) throw new Error("Failed to fetch floor plan");
      const data = await res.json();
      const floorPlan = data.floorPlan;
      set((state) => {
        const existing = state.floorPlans.findIndex((f) => f.id === id);
        const updated = existing >= 0
          ? state.floorPlans.map((f) => (f.id === id ? floorPlan : f))
          : [...state.floorPlans, floorPlan];
        return { floorPlans: updated, loading: false };
      });
      return floorPlan;
    } catch (err) {
      console.error("[mapStore] fetchFloorPlanById error:", err);
      set({ error: err.message, loading: false });
      return null;
    }
  },
  clearError: () => set({ error: null }),
}));

export default useMapStore;
